// Sprint 6-home-redesign: чистая логика Главной (тестируемая без render-харнесса).
// deriveAttention — единый источник и для списка «Требует внимания», и для
// хиро-счётчика (иначе расхождение с summary.out_of_range — G4 из PRD-ассессмента).

import { sortByTier } from '@/lib/biomarkerStatus';
import type { ListScreenState } from '@/lib/markerNavState';
import type {
  BiomarkerListFilter,
  DashboardBiomarkerItem,
  DashboardCategory,
  DashboardSummary,
  HeadlineChange,
} from '@/api/types';

export const ATTENTION_LIMIT = 3;
export const CHANGES_LIMIT = 3;

export function deriveAttention(
  categories: DashboardCategory[] | null | undefined,
): DashboardBiomarkerItem[] {
  const items = (categories ?? []).flatMap((c) => c.items);
  const outOfRange = items.filter((i) => i.clinical_status === 'out_of_range');
  return sortByTier(outOfRange);
}

/** Слово-подпись стороны отклонения (стрелку НЕ рисуем — правило разведения). */
export function directionLabel(direction: 'above' | 'below' | null): string {
  if (direction === 'below') return 'ниже нормы';
  if (direction === 'above') return 'выше нормы';
  return 'вне нормы';
}

/** «Требует внимания»: показываем топ-N, при >N — ссылка «Показать все». */
export function attentionView(items: DashboardBiomarkerItem[]): {
  shown: DashboardBiomarkerItem[];
  total: number;
  hasMore: boolean;
} {
  return {
    shown: items.slice(0, ATTENTION_LIMIT),
    total: items.length,
    hasMore: items.length > ATTENTION_LIMIT,
  };
}

/** «Что изменилось»: жёсткий cap top-N на стороне компонента (не полагаемся на backend). */
export function limitChanges(changes: HeadlineChange[]): HeadlineChange[] {
  return changes.slice(0, CHANGES_LIMIT);
}

/** Хиро-статус: attentionCount === 0 → «всё в норме»; иначе — «Сводка». */
/**
 * Сколько строк в каталоге показателей.
 *
 * R-1/R-2: `no_range` сюда НЕ входит — с этой версии он живёт только в
 * «Дополнительных», куда ведут и столбик сводки, и строка «Без оценки».
 * Одна функция на две витрины: кнопка «Все показатели · N» на Главной и чип
 * «Все · N» в каталоге обязаны показывать одно число, иначе кнопка обещает
 * одно, а экран отдаёт другое. Прецеденты расхождения: HEAL-13, HEAL-37.
 */
export function catalogMarkersCount(summary: DashboardSummary): number {
  return summary.in_range + summary.attention + summary.out_of_range;
}

/**
 * Хиро-статус Главной.
 *
 * `allNormal` означает буквально «все показатели в норме», а не «красных нет».
 *
 * Раньше условием было `attentionCount === 0`, то есть отсутствие 🔴. При
 * одиннадцати 🟠 и шестнадцати неоценённых экран писал «Все показатели в
 * норме», а строкой ниже — «37 из 64»: заголовок сам себе противоречил, и
 * сводка пропадала ровно тогда, когда ей было что показать. Решение PM
 * (05.10.2026): сводка выводится всегда, в том числе на чистом результате.
 */
export function heroView(
  summary: DashboardSummary,
  extrasCount: number,
  attentionCount: number,
): { allNormal: boolean; inRange: number; unknown: number; total: number } {
  const inRange = summary.in_range;
  // HEAL-30: `no_range` больше НЕ прибавляется здесь — с этой версии он уже
  // входит в extras_count на бэкенде. Иначе три показателя без нормы считались
  // бы дважды, а раньше была обратная беда: они попадали в «Без оценки» на
  // Главной, но не в список за ней (22 против 19).
  const unknown = extrasCount;
  return {
    // Ни оранжевых, ни красных, ни неоценённых — только тогда фраза правдива.
    allNormal: summary.attention === 0 && attentionCount === 0 && unknown === 0,
    inRange,
    unknown,
    // HEAL-37: 🟠 стал отдельным бакетом и вышел из in_range — без него сумма
    // столбцов перестала бы сходиться с итогом над ними.
    total: inRange + summary.attention + attentionCount + unknown,
  };
}

/**
 * Состояние вкладки «Маркеры» для перехода с Главной. attention → «Вне нормы» на
 * сегменте «По категориям»; changes/«Все показатели» → filter='all' БЕЗ сегмента
 * (дефолтный вид, после HEAL-24 это тоже «По категориям»). Ключевое: всегда пишем
 * filter явно, чтобы не унаследовать старый `out_of_range` из предыдущего перехода (P1).
 */
export function markersNavState(
  filter: BiomarkerListFilter,
  categoriesSegment: boolean,
): ListScreenState {
  return {
    scrollTop: 0,
    openByCode: {},
    filter,
    ...(categoriesSegment ? { segment: 'categories' as const } : {}),
  };
}
